import { useEffect, useLayoutEffect, useState, useRef } from "react"
import { createPortal } from "react-dom"
import { mvSubscribe } from "./utils"
import { useConfigurator, writePricePolicy } from "./context"
import Configurator from "./index"
import { useLocale } from "contexts/Locale"
import { useUser } from "contexts/User"
import { useNavigation } from "contexts/Navigation"
import { useDictionary } from "contexts/Dictionary"
import { useMotionValue, useScroll } from "framer-motion"
import styled from "@emotion/styled"
import getMediaQuery from "css/breakpoints"
import { visuallyHidden, headline30, legend100 } from "css/text"
import { buttonText, buttonReset, buttonIcon, translucentDark } from "css/buttons"
import { Icon } from "components/icon/Icon"
import { hashIt } from "./hash"
import { usePanelModal } from "contexts/PanelModal"
import { FocusScope } from "@react-aria/focus"
import { fullGrid } from "css/grid"
import { Close as ModalClose } from "components/modal/ModalContainer"
import useScrollLock from "hooks/useScrollLock"

const PPMainX = styled.aside`
  ${fullGrid}
  position: fixed;
  width: 100%;
  height: 100%;
  overflow: hidden;
  top: 0;
  left: 0;
  z-index: 1000;

  box-sizing: border-box;
  background: rgba(0,0,0,.6);
  padding: 2rem 0;

  & > div {
    position: relative;
    grid-column: main;

    ${getMediaQuery("m")} {
      grid-column: col 2 / col -2;
    }

    max-height: 100%;
    overflow: hidden;
    background: #fff;
    border-radius: 2rem;
    box-sizing: border-box;
    padding-block: 3rem;
    display: grid;
    grid-template-rows: 1fr;
    grid-template-columns: 1fr;
    align-items: center;


    & > div {
      position: relative;
      grid-row: 1;
      grid-column: 1;
      display: grid;
      max-height: 100%;
      grid-template-rows: 1fr auto;
      grid-template-columns: 1fr;
      overflow: hidden;

        & ul {
          position: relative;
          display: flex;
          flex-direction: row;
          box-sizing: border-box;
          width: 100%;
          max-height: 100%;
          overflow-x: auto;
          scrollbar-width: none;
          scroll-behavior: auto;
          pointer-events: auto;
          scroll-behavior: smooth;

          &::-webkit-scrollbar {
            display: none;
          }

          scroll-snap-type: x mandatory;

          & li {
            padding-inline: var(--grid-col-unit);
  
            ${getMediaQuery("m")} {
              padding-inline: 6.25rem;
            }
            box-sizing: border-box;
            position: relative;
            width: 100%;
            max-height: 100%;
            overflow-y: auto;
            flex: 0 0 100%;
            scroll-snap-align: center;
            scroll-snap-stop: always;
          }
        }

        & > div {
          display: inline-flex;
          justify-self: center;
          margin-block: 1.5rem .5rem;

          & button {
            --buton-size: 10px;
            posirtion: relative;
            ${buttonReset}
            --spacing: var(--height);
            padding: 1rem;
            border-radius: 1rem;
            margin-inline: .5rem;
            &::after {
              content: "";
              position: absolute;
              transform: translate(calc(var(--buton-size) * (-.5)), calc(var(--buton-size) * (-.5)));
              background: #000;
              width: var(--buton-size);
              height: var(--buton-size);
              border-radius: var(--buton-size);
              overflow: hidden;
            }
            &.pp-on {
              &::after {
                background: grey;
              }
            }
            & span {
              ${visuallyHidden}
            }
          }
        }
      }
    }
  }
`

const PPMainY = styled.aside`
  ${fullGrid}
  position: fixed;
  width: 100%;
  height: 100%;
  overflow: hidden;
  top: 0;
  left: 0;
  z-index: 1000;

  box-sizing: border-box;
  background: rgba(0,0,0,.6);
  padding: 2rem 0;

  & > div {
    position: relative;
    grid-column: main;
    padding-inline: var(--grid-col-unit);

    ${getMediaQuery("m")} {
      grid-column: col 2 / col -2;
      padding-inline: 6.25rem;
    }

    max-height: 100%;
    overflow: hidden;
    background: rgb(var(--pure-white));
    border-radius: 2rem;
    box-sizing: border-box;
    align-items: center;
    display: grid;

    & > div {
      max-height: 100%;
      overflow-y: auto;
      scrollbar-width: none;
      pointer-events: all;

      ::-webkit-scrollbar {
        display: none;
      }

      & h2 {
        margin-block-start: 6.25rem;
        &:not(:first-of-type) {
          margin-block-start: 3.75rem;
        }
      }

      & > div {
        &:last-of-type {
          margin-block-end: 6.25rem;
        }
      }
    }

    &::before, 
    &::after {
      content: "";
      position: absolute;
      left: 0;
      width: 100%;
      height: min(25vh,5rem);
      pointer-events: none;
    }
    &::before {
      top: 0;
      background: linear-gradient(0deg, rgb(var(--pure-white) / .3), rgb(var(--pure-white) / 1) 100%);
    }
    &::after {
      bottom: 0;
      background: linear-gradient(0deg, rgb(var(--pure-white) / 1), rgb(var(--pure-white) / .3) 100%);
    }

    &.prevonly.nextonly {
      &::before, 
      &::after {
        display: none;
      }
    }
    &.prevonly {
      &::after {
        display: none;
      }
    }
    &.nextonly {
      &::before {
        display: none;
      }
    }
  }
}
`

const Heading = styled.h2`
  ${headline30}
  margin-block-end: 1ex;
`

const Paragraph = styled.div`
  ${legend100}
  p:not(:last-of-type) {
    margin-block-end: 2ex;
  }
`

const Close = styled.button`
  ${buttonIcon}
  ${translucentDark}
//  --spacing: calc(var(--outer-margin) - var(--height) / 2);

  --spacing: calc(var(--outer-margin) - var(--grid-gap) / 2 - var(--height) / 2);

  ${getMediaQuery("m")} {
//    --spacing: 3.125rem;
    --spacing: calc(var(--outer-margin) / 2 - var(--height) / 2);
  }
  inset-block-start: var(--spacing);
  inset-inline-end: var(--spacing);
  position: absolute;
  z-index: 29;
  animation-name: close-reveal;
  animation-fill-mode: both;
  animation-duration: 400ms;
  animation-timing-function: cubic-bezier(0.61, 1, 0.88, 1);
  animation-delay: 0ms;

  @keyframes close-reveal {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`

export default function ConfiguratorPlayer({ initialState }) {
  const [active, setActive] = useState(null)
  const { route, fromui, launched, cta } = useConfigurator()
  const locale = useLocale()
  const dictionary = useDictionary()
  const { panelModalState } = usePanelModal()

  function kill() {
    if (fromui.get()) hashIt("")
    setTimeout(() => {
      route.set(null)
    }, 10)
  }

  function onClick(e) {
    fromui.set(true)
    kill()
  }

  function onRoute(v) {
    //    console.log("ConfiguratorPlayer onRoute", v)
    let [family, startrmc, startstep] = (v || "").split("/").filter(v => !!v?.length)
    //    console.log("ConfiguratorPlayer onRoute split family, startrmc, startstep", family, startrmc, startstep)
    const hash = window.location.hash.split("").slice(1).join("")
    panelModalState.set(!!v)
    //    console.log("ConfiguratorPlayer onRoute hash", hash)
    if (hash.length) {
      const [cfg, ...all] = hash.split("/")
      if (cfg === "cfg") {
        //        console.log("ConfiguratorPlayer onRoute hash length & valid")
        launched.set(true)
        if (!v) return route.set(all.join("/"))
        else v = all.join("/")
      }
    }
    //    console.log("ConfiguratorPlayer onRoute after hash", v)
    if (!v) {
      //      console.log("ConfiguratorPlayer onRoute no route", v)
      launched.set(false)
      fromui.set(false)
      if (cta.current) cta.current.focus()

      return setActive(null)
    }
    ;[family, startrmc, startstep] = v.split("/").filter(v => !!v?.length)
    const credentials = initialState?.env?.withCredentials ? "include" : "same-origin"
    const istate = initialState[locale.current.codes.www]
    const families = istate?.configurator?.[0]?.families
    const f = families?.find(f => f.family_code === family)
    const intro = f?.intro?.[0]
    const color_set = f?.params?.[0]?.color_set
    startrmc = startrmc || f?.params?.[0]?.rmc

    const cfgprops = {
      family: family,
      startrmc: startrmc,
      startstep: startstep,
      bkgmode: true,
      fromui: fromui,
      launched: launched,
      route: route,
      clear: kill,
      onClose: onClick,
      intro: intro,
      color_set: color_set ? `page-color-set-rolex-v7-color-${color_set}` : null,
      className: startstep ? "wv_straight" : null,
      credentials: credentials
    }

    setActive(
      <>
        <Configurator {...cfgprops}>
          <Close className='wv_reveal dark-theme' onClick={onClick} aria-label={dictionary.popinClose()}>
            <Icon type='close' />
          </Close>
        </Configurator>
        <PricePolicy />
      </>
    )
  }
  useLayoutEffect(() => mvSubscribe(route, onRoute), [])

  useEffect(() => kill, [])

  return process.browser && active ? createPortal(active, document.body) : null
}

function PricePolicyPageButton({ index, scrollTo, current_page }) {
  const rfmain = useRef(null)

  function onCurrentPage(v) {
    rfmain.current.classList[v === index ? "add" : "remove"]("pp-on")
  }
  useLayoutEffect(() => mvSubscribe(current_page, onCurrentPage), [])

  function onClick(e) {
    scrollTo(index)
  }

  return (
    <button ref={rfmain} onClick={onClick}><span>{`Page ${index}`}</span></button>
  )
}

function PricePolicyContents_X({ items }) {
  const dictionary = useDictionary()
  const { price_policy } = useConfigurator()
  const current_page = useMotionValue(0)
  const rfmain = useRef(null)

  function onClick(e) {
    price_policy.set(true)
  }

  function scrollTo(i) {
    const col = [...rfmain.current.querySelectorAll("li")]
    rfmain.current.scrollLeft = col[i].offsetLeft
  }

  const { scrollXProgress } = useScroll({
    container: rfmain,
    axis: "x",
    offset: ["start end", "end end"],
  })

  function onScrollXProgress(v) {
    const l = items.length
    const index = Math.ceil(l * v) - 1
    current_page.set(index)
    //    console.log("indexindex", v, index)
  }
  useLayoutEffect(() => mvSubscribe(scrollXProgress, onScrollXProgress), [items])

  return (
    <PPMainX>
      <FocusScope contain>
        <div>
          <ModalClose className='light-theme' onClick={onClick} aria-label={dictionary.popinClose()}>
            <Icon type='close' />
          </ModalClose>
          <div>
            <ul ref={rfmain}>
              {items.map((item, i) => (
                <li key={`pp-${i}`}>
                  <Heading>{item.title}</Heading>
                  <Paragraph dangerouslySetInnerHTML={{ __html: item.text }} />
                </li>
              ))}
            </ul>
            <div>
              {items.map((item, i) => (
                <PricePolicyPageButton key={`pp-page-${i}`} current_page={current_page} scrollTo={scrollTo} index={i} />
              ))}
            </div>
          </div>
        </div>
      </FocusScope>
    </PPMainX>
  )
}

function PricePolicyContents_Y({ items }) {
  const dictionary = useDictionary()
  const { price_policy } = useConfigurator()
  const rfmain = useRef(null)
  const rfscroll = useRef(null)
  const modalRef = useRef(null)
  const scrollLock = useScrollLock()

  function onClick(e) {
    price_policy.set(true)
  }
  /*
    function scrollTo(i) {
      const col = [...rfmain.current.querySelectorAll("li")]
      rfmain.current.scrollLeft = col[i].offsetLeft
    }
  */
  const { scrollY } = useScroll({
    container: rfscroll,
    axis: "y",
    offset: ["start end", "end end"],
  })

  function onScrollY(y) {
    const offset = 2
    const scrollmax = rfscroll.current.scrollHeight - rfscroll.current.offsetHeight

    rfmain.current.classList[y <= offset ? "add" : "remove"]("nextonly")
    rfmain.current.classList[y >= (scrollmax - offset) ? "add" : "remove"]("prevonly")
  }
  useLayoutEffect(() => mvSubscribe(scrollY, onScrollY), [items])

  const onkeydown = e => {
    e.preventDefault()
    if (e.key === "Enter") onClick()
  }

  const onClickOutside = e => {
    e.stopPropagation()
    // If the click is outside the modal, close it
    const path = e.nativeEvent.composedPath ? e.nativeEvent.composedPath() : e.nativeEvent.path
    if (!path.includes(modalRef.current)) {
      onClick()
    }
  }

  const onClickCont = e => {
    e.stopPropagation()
  }

  useEffect(() => {
    const r = modalRef.current
    scrollLock.lock(r)
    return () => scrollLock.unlock(r)
  }, [])

  useLayoutEffect(() => {
    const r = rfmain.current

    const resizeObserver = new ResizeObserver(entries => {
      for (const entry of entries) {
        if (entry.contentBoxSize) {
          onScrollY(scrollY.get())
        }
      }
    })

    resizeObserver.observe(r)

    return () => resizeObserver.unobserve(r)
  }, [])

  return (
    <PPMainY ref={modalRef}>
      <FocusScope contain>
        <div ref={rfmain}>
          <ModalClose aria-label={dictionary.popinClose()} onClick={onClick} icon='close' className='icon translucent-light' onKeyDown={onkeydown}>
            <span>{dictionary.popinClose()}</span>
          </ModalClose>
          <div ref={rfscroll}>
            {items.map((item, i) => (
              <PricePolicyBlock key={`pp-${i}`} {...item} />
            ))}
          </div>
        </div>
      </FocusScope>
    </PPMainY>
  )
}

async function load(path) {
  return Promise.resolve(new URL(path, process.browser ? global.location.origin : process.env.REVERSE_PROXY_URL))
    .then(url => new Request(url))
    .then(request => fetch(request))
    .then(response => response.json())
    .then(body => {
      return [null, body]
    })
    .catch(err => [err, {}])
}

const PRICE_POLICIES = ["IT"]

function PricePolicyBlock({ title, text }) {
  return (
    <>
      <Heading>{title}</Heading>
      <Paragraph dangerouslySetInnerHTML={{ __html: text }} />
    </>
  )
}

export function PricePolicy() {
  const [active, setActive] = useState(null)
  const { price_policy } = useConfigurator()
  const { countryCode } = useUser()
  const navigation = useNavigation()
  const locale = useLocale()

  async function onPricePolicy(v) {
    if (v || !PRICE_POLICIES.includes(countryCode)) return setActive(null)
    const path = navigation.localize(`/legalpriceinfo/${countryCode}.extra.json`, locale.current.codes.www)
    const data = await load(path).catch(err => console.log("••• err", err))
    let info = data?.[1]?.[0]
    if (!info) return setActive(null)
    writePricePolicy(true)
    info = [
      {
        title: info.price_title,
        text: info.legal_notice
      },
      {
        title: info.scarcity_title,
        text: info.scarcity_text
      }
    ]
    setActive(<PricePolicyContents_Y items={info} />)
  }

  useLayoutEffect(() => mvSubscribe(price_policy, onPricePolicy), [])

  return process.browser && active ? createPortal(active, document.body) : null
}
